import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Fence, LogIn, UserPlus } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_contractor-pipeline/artifacts/xmrl9jcs_image_1764138784069.png";

export default function LoginPage() {
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) {
        await register(name, email, password);
      } else {
        await login(email, password);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8 animate-fade-in">
          <img src={LOGO_URL} alt="Logo" className="w-20 h-20 rounded-2xl mb-4 shadow-sm" />
          <h1 className="text-2xl font-extrabold text-zinc-900 tracking-tight text-center">
            Picture Perfect CRM
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Fence N Construction Inc.</p>
        </div>

        <Card className="border border-zinc-200 shadow-none animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-bold text-zinc-900">
              {isRegister ? "Create Account" : "Sign In"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-zinc-700">Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required={isRegister}
                    data-testid="register-name-input"
                    className="border-zinc-300 focus:ring-2 focus:ring-orange-600"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-zinc-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  data-testid="login-email-input"
                  className="border-zinc-300 focus:ring-2 focus:ring-orange-600"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-zinc-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  data-testid="login-password-input"
                  className="border-zinc-300 focus:ring-2 focus:ring-orange-600"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="auth-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold h-11"
                data-testid="login-submit-btn"
              >
                {loading ? "..." : isRegister ? (
                  <><UserPlus className="w-4 h-4 mr-2" /> Create Account</>
                ) : (
                  <><LogIn className="w-4 h-4 mr-2" /> Sign In</>
                )}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                onClick={() => { setIsRegister(!isRegister); setError(""); }}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                data-testid="toggle-auth-mode"
              >
                {isRegister ? "Already have an account? Sign in" : "Need an account? Register"}
              </button>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-zinc-400 text-center mt-6">
          Calgary's #1 Fence Contractors - 10 Year Warranty
        </p>
      </div>
    </div>
  );
}
